import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Stack } from "@mui/joy";
import { theme } from "styles/theme";
import CustomTypography from "components/common/CustomTypography";
import SelectCheckbox from "../../components/onboarding/SelectCheckBox";
import SignatureCanvas from "react-signature-canvas";
import { useRecoilState } from "recoil";
import { userAtom } from "../../recoil/userAtom";
import Typography from "@mui/joy/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BottomNextButton } from "../common/BottomNextButton";
import { commitmentApi } from "../../api/commitmentApi";
import { sendMessage } from "../../utils/webview";
import { useNavigate } from "react-router-dom";
import useIsInappWebview from "../../hooks/useIsInappWebview";
import FullPageWithHeaderAndProgressV2 from "../../pages/container/FullPageWithHeaderAndProgressV2";
import { extractHexColor } from "../../styles/hexOpacity";
import useMixpanelWeb from "../../hooks/useMixpanelWeb";
import { USER_DETAIL_ENDPOINT, USER_DOMAIN, userApi } from "../../api/userApi";
import { isAuthReady } from "../../api";

// 옵션 리스트
const options: Option[] = [
  { id: 1, text: "변화를 믿고 시간을 투자하겠습니다." },
  {
    id: 2,
    text: "포기하지 않고 꾸준히 나아가겠습니다.",
  },
  {
    id: 3,
    text: "완벽보다 작은 성취를 인정하겠습니다.",
  },
  {
    id: 4,
    text: "스스로를 다그치기보다 격려하겠습니다.",
  },
  {
    id: 5,
    text: "작은 어려움도 코치와 함께하겠습니다.",
  },
];

interface Option {
  id: number;
  text: string;
}

export default function SelfCommitmentSubmitPage() {
  const navigate = useNavigate();
  const isInappWebview = useIsInappWebview();

  const [user, setUser] = useRecoilState(userAtom);

  const [multiSelectionIdList, setMultiSelectionIdList] = useState<number[]>(
    () => {
      if (typeof window === "undefined") return [];
      try {
        const ns = `commitment:${user?.userId ?? "anon"}`;
        const currentKey = `${ns}:checks`;
        const anonKey = `commitment:anon:checks`;
        const raw =
          localStorage.getItem(currentKey) ?? localStorage.getItem(anonKey);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
  );

  const [signatureDataUrl, setSignatureDataUrl] = useState<string | null>(
    () => {
      if (typeof window === "undefined") return null;
      try {
        const ns = `commitment:${user?.userId ?? "anon"}`;
        const currentKey = `${ns}:signature`;
        const anonKey = `commitment:anon:signature`;
        return (
          localStorage.getItem(currentKey) ?? localStorage.getItem(anonKey)
        );
      } catch {
        return null;
      }
    }
  );

  const [isSigned, setIsSigned] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      const ns = `commitment:${user?.userId ?? "anon"}`;
      const currentKey = `${ns}:signature`;
      const anonKey = `commitment:anon:signature`;
      const saved =
        localStorage.getItem(currentKey) ?? localStorage.getItem(anonKey);
      return !!saved;
    } catch {
      return false;
    }
  });
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [canvasKey, setCanvasKey] = useState(Date.now());

  const sigRef = useRef<any>(null);
  const signatureContainerRef = useRef<HTMLDivElement>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [pixelRatio, setPixelRatio] = useState(1);

  // sessionStorage 키 네임스페이스 (사용자별 분리)
  const STORAGE_PREFIX = "commitment";
  const storageNS = `${STORAGE_PREFIX}:${user?.userId ?? "anon"}`;
  const CHECKS_KEY = `${storageNS}:checks`;
  const SIGNATURE_KEY = `${storageNS}:signature`;
  const VISIBLE_COUNT_KEY = `${storageNS}:visibleCount`;
  // anon 네임스페이스(사용자 정보 로딩 전) 키
  const ANON_NS = `${STORAGE_PREFIX}:anon`;
  const ANON_CHECKS_KEY = `${ANON_NS}:checks`;
  const ANON_SIGNATURE_KEY = `${ANON_NS}:signature`;
  const ANON_VISIBLE_COUNT_KEY = `${ANON_NS}:visibleCount`;
  // 서명 복원은 캔버스 준비 후 단 한 번만
  const restoredRef = useRef(false);

  const queryClient = useQueryClient();

  useQuery(
    [USER_DOMAIN, USER_DETAIL_ENDPOINT],
    useCallback(() => userApi.getDetail(), []),
    {
      enabled: isAuthReady(user),

      onSuccess: (data) => {
        if (data && data.result) {
          //결제 탭 닫기
          setUser((user) => ({
            ...user,
            ...data.result,
          }));
        }
      },
    }
  );

  // 진행 단계 상태: 세션 저장값 또는 체크 상태 기반(한번 열린 단계는 닫히지 않음)
  const [visibleOptionsCount, setVisibleOptionsCount] = useState<number>(() => {
    const maxDisplay = options.length + 1;
    try {
      // 1) 세션에서 우선 복원
      const saved = localStorage.getItem(VISIBLE_COUNT_KEY);
      if (saved) {
        const n = parseInt(saved, 10);
        if (!Number.isNaN(n)) {
          return Math.min(maxDisplay, Math.max(1, n));
        }
      }
      // 2) fallback: 체크 상태 기반
      const savedChecksRaw = localStorage.getItem(CHECKS_KEY);
      const list = savedChecksRaw
        ? (JSON.parse(savedChecksRaw) as number[])
        : [];
      const maxChecked =
        Array.isArray(list) && list.length > 0 ? Math.max(...list) : 0;
      let initial = Math.min(maxDisplay, Math.max(1, maxChecked + 1));
      // 3) 저장된 서명이 있으면 서명 단계(6)까지는 항상 보이게
      const savedSig = localStorage.getItem(SIGNATURE_KEY);
      if (savedSig) {
        initial = Math.max(initial, 6);
      }
      return initial;
    } catch {
      return 1;
    }
  });
  const showSignature = visibleOptionsCount >= 6;

  const sentMixpanelRef = useRef(false);

  const setMixPanel = useMixpanelWeb({
    eventName: "서약 진입",
  });

  useEffect(() => {
    if (!sentMixpanelRef.current) {
      setMixPanel();
      sentMixpanelRef.current = true;
    }
  }, []);

  useEffect(() => {
    if (!showSignature) return;
    if (signatureContainerRef.current) {
      const { width, height } =
        signatureContainerRef.current.getBoundingClientRect();
      if (width > 0 && height > 0) {
        setCanvasSize({ width, height });
      }
    }
  }, [showSignature]); // 서명 영역 표시 시 계산

  // Ensure high-DPI clarity for the signature canvas
  useEffect(() => {
    if (!sigRef.current) return;
    const canvas: HTMLCanvasElement = sigRef.current.getCanvas();
    // Set the canvas's actual pixel size to account for device pixel ratio
    canvas.width = Math.max(1, Math.floor(canvasSize.width * pixelRatio));
    canvas.height = Math.max(1, Math.floor(canvasSize.height * pixelRatio));
    // Keep visual size the same via CSS
    canvas.style.width = `${canvasSize.width}px`;
    canvas.style.height = `${canvasSize.height}px`;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    // Reset any existing transforms, then scale for crisp strokes
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(pixelRatio, pixelRatio);
  }, [canvasSize.width, canvasSize.height, pixelRatio, canvasKey]);

  useEffect(() => {
    if (isInappWebview) {
      const background = extractHexColor(theme.vars.palette.Background.Level1);

      sendMessage(
        JSON.stringify({
          event: "set_safearea_color",
          params: {
            topColor: `${background}`,
            bottomColor: `${background}`,
          },
        })
      );
    }

    // Update pixel ratio on mount; in case of zoom or DPR changes, you may extend this.
    if (typeof window !== "undefined") {
      const dpr = Math.max(window.devicePixelRatio || 1, 1);
      setPixelRatio(dpr);
    }
  }, []);

  // 사용자 정보 로딩 후 anon 네임스페이스의 저장값을 사용자 네임스페이스로 마이그레이션
  useEffect(() => {
    if (typeof window === "undefined") return;
    // 아직 사용자 ID가 없으면 대기
    if (storageNS.endsWith(":anon")) return;

    try {
      const anonChecks = localStorage.getItem(ANON_CHECKS_KEY);
      const anonSig = localStorage.getItem(ANON_SIGNATURE_KEY);
      const anonVisible = localStorage.getItem(ANON_VISIBLE_COUNT_KEY);

      const curChecks = localStorage.getItem(CHECKS_KEY);
      const curSig = localStorage.getItem(SIGNATURE_KEY);
      const curVisible = localStorage.getItem(VISIBLE_COUNT_KEY);

      // 현재 사용자 키에 값이 없을 때만 anon 값을 승계
      if (anonChecks && !curChecks) {
        localStorage.setItem(CHECKS_KEY, anonChecks);
      }
      if (anonSig && !curSig) {
        localStorage.setItem(SIGNATURE_KEY, anonSig);
      }
      if (anonVisible && !curVisible) {
        localStorage.setItem(VISIBLE_COUNT_KEY, anonVisible);
      }

      // 마이그레이션 후 anon 값 정리
      if (anonChecks) localStorage.removeItem(ANON_CHECKS_KEY);
      if (anonSig) localStorage.removeItem(ANON_SIGNATURE_KEY);
      if (anonVisible) localStorage.removeItem(ANON_VISIBLE_COUNT_KEY);
    } catch {
      // 스토리지 접근 실패 무시
    }
  }, [
    storageNS,
    CHECKS_KEY,
    SIGNATURE_KEY,
    VISIBLE_COUNT_KEY,
    ANON_CHECKS_KEY,
    ANON_SIGNATURE_KEY,
    ANON_VISIBLE_COUNT_KEY,
  ]);

  // 체크박스 저장값 복원
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const savedChecks = localStorage.getItem(CHECKS_KEY);
      if (savedChecks) {
        const parsed = JSON.parse(savedChecks) as number[];
        if (Array.isArray(parsed)) {
          setMultiSelectionIdList(parsed);
        }
      }
    } catch {
      // 스토리지 접근 실패 무시
    }
  }, [CHECKS_KEY]);

  // 서명 이미지 복원 (캔버스 준비 후 1회)
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!showSignature) return;
    if (restoredRef.current) return;
    if (!sigRef.current) return;
    if (canvasSize.width <= 0 || canvasSize.height <= 0) return;

    try {
      const savedSig = localStorage.getItem(SIGNATURE_KEY);
      if (savedSig) {
        sigRef.current.clear();
        sigRef.current.fromDataURL(savedSig);
        setSignatureDataUrl(savedSig);
        setIsSigned(true);
      }
      restoredRef.current = true;
    } catch {
      // 스토리지 접근 실패 무시
    }
  }, [
    showSignature,
    SIGNATURE_KEY,
    canvasSize.width,
    canvasSize.height,
    pixelRatio,
    canvasKey,
  ]);

  // 체크박스 변경 시 저장
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      localStorage.setItem(CHECKS_KEY, JSON.stringify(multiSelectionIdList));
    } catch {
      // 무시
    }
  }, [multiSelectionIdList, CHECKS_KEY]);

  // 체크 진행도에 따라 다음 항목 노출(한번 보인 단계는 유지)
  useEffect(() => {
    const maxDisplay = options.length + 1;
    const maxCheckedId =
      multiSelectionIdList.length > 0 ? Math.max(...multiSelectionIdList) : 0;
    const desiredNext = Math.min(maxDisplay, Math.max(1, maxCheckedId + 1));

    setVisibleOptionsCount((prev) => {
      const next = Math.max(prev, desiredNext);
      if (next !== prev) {
        try {
          localStorage.setItem(VISIBLE_COUNT_KEY, String(next));
        } catch {
          // 무시
        }
      }
      return next;
    });
  }, [multiSelectionIdList, VISIBLE_COUNT_KEY]);

  // 서명 dataURL 변경 시 저장/삭제
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (signatureDataUrl) {
        localStorage.setItem(SIGNATURE_KEY, signatureDataUrl);
      } else {
        localStorage.removeItem(SIGNATURE_KEY);
      }
    } catch {
      // 무시
    }
  }, [signatureDataUrl, SIGNATURE_KEY]);

  const handleOptionChange = (id: number) => {
    setMultiSelectionIdList((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]
    );
  };

  const handleClear = () => {
    setCanvasKey(Date.now()); // key를 변경하여 강제로 리마운트
    setIsSigned(false);
    setSignatureDataUrl(null);
    try {
      localStorage.removeItem(SIGNATURE_KEY);
    } catch {
      // 무시
    }
  };

  const handleSignatureBegin = () => {
    setIsSigned(true);
  };

  const handleSignatureEnd = () => {
    if (!sigRef.current) return;
    const empty = sigRef.current.isEmpty();
    setIsSigned(!empty);
    if (!empty) {
      const dataUrl = sigRef.current.getCanvas().toDataURL("image/png");
      setSignatureDataUrl(dataUrl);
    }
  };

  const { mutate: submit } = useMutation({
    mutationFn: (vars: { signature: string }) =>
      commitmentApi.submit({
        signature: vars.signature,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries([USER_DOMAIN, USER_DETAIL_ENDPOINT]);

      setIsSubmitLoading(false);

      // 성공 시 세션 저장값 정리
      try {
        localStorage.removeItem(SIGNATURE_KEY);
        localStorage.removeItem(CHECKS_KEY);
      } catch {
        // 무시
      }

      handleNext();
    },
    onError: () => {
      setIsSubmitLoading(false);

      handleNext();
    },
  });

  const handleSubmitSignature = () => {
    // 서명 제출
    handleSubmit();
  };

  const handleSubmit = () => {
    // 유효성 검사 먼저 수행 (로딩 표시 전)
    const canvasEmpty = sigRef.current ? sigRef.current.isEmpty() : true;
    const hasSignature = !!signatureDataUrl || !canvasEmpty;
    if (!hasSignature) return;

    const dataUrl =
      signatureDataUrl ?? sigRef.current!.getCanvas().toDataURL("image/png");
    setSignatureDataUrl(dataUrl);

    // 요청 시작: 로딩 표시 활성화, 모달은 유지하여 로딩을 노출
    setIsSubmitLoading(true);
    submit({ signature: dataUrl });
    // 모달 닫기 및 화면 닫기는 onSuccess에서 처리합니다.
  };

  const allAgreed = multiSelectionIdList.length === options.length;
  const submitDisabled = !allAgreed || !isSigned;

  const displayDate = new Date().toLocaleDateString("ko-KR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const handleNext = () => {
    if (isInappWebview) {
      sendMessage(
        JSON.stringify({
          event: "navigate_back",
        })
      );
    } else {
      navigate("/", { replace: true });
    }
  };

  const body = (
    <Box sx={{ paddingBottom: "120px" }}>
      <Stack
        spacing={10}
        sx={{ flex: 1, display: "flex", flexDirection: "column" }}
      >
        <Stack spacing={1}>
          <Stack spacing={5}>
            <CustomTypography
              variant="Subtitle"
              fontWeight={500}
              sx={{
                color: theme.vars.palette.Content.Strong,
              }}
            >
              {
                "지금부터 스스로와 작은 약속을 맺으며, 100일 간의 여정을 시작하겠습니다."
              }
            </CustomTypography>
            {user?.nickname && (
              <CustomTypography
                variant="Body"
                fontWeight={500}
                sx={{
                  color: theme.vars.palette.Content.Medium,
                }}
              >
                {`나 ${user?.nickname}${(() => {
                  const raw = user?.nickname || "";
                  const s = raw.trim();
                  if (!s) return "는";
                  const ch = s.charCodeAt(s.length - 1);
                  const isHangul = ch >= 0xac00 && ch <= 0xd7a3;
                  if (isHangul) {
                    const hasBatchim = (ch - 0xac00) % 28 !== 0;
                    return hasBatchim ? "은" : "는";
                  }
                  const last = s[s.length - 1];
                  if (/[A-Za-z]/.test(last)) {
                    return /[AEIOUaeiou]/.test(last) ? "는" : "은";
                  }
                  return "는";
                })()}...`}
              </CustomTypography>
            )}
          </Stack>
          <Stack spacing={0.5}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {options
                .filter(
                  (option) =>
                    option.id <= Math.min(visibleOptionsCount, options.length)
                )
                .map((option) => (
                  <SelectCheckbox
                    key={option.id}
                    checked={multiSelectionIdList.includes(option.id)}
                    onChange={() => handleOptionChange(option.id)}
                    label={option.text}
                  />
                ))}
            </Box>
            {showSignature && (
              <Box sx={{ mt: 2 }}>
                <Box
                  ref={signatureContainerRef}
                  sx={{
                    position: "relative",
                    borderRadius: "16px",
                    backgroundColor: theme.vars.palette.Surface.Forward,
                    height: 200,
                    overflow: "hidden",
                  }}
                >
                  {canvasSize.width > 0 && canvasSize.height > 0 && (
                    <SignatureCanvas
                      key={canvasKey}
                      ref={sigRef}
                      onBegin={handleSignatureBegin}
                      onEnd={handleSignatureEnd}
                      penColor={"black"}
                      minWidth={1.5}
                      maxWidth={1.5}
                      canvasProps={{
                        // Use high-DPI internal resolution while keeping visual size consistent
                        width: Math.max(
                          1,
                          Math.floor(canvasSize.width * pixelRatio)
                        ),
                        height: Math.max(
                          1,
                          Math.floor(canvasSize.height * pixelRatio)
                        ),
                        style: {
                          display: "block",
                          touchAction: "none",
                          backgroundColor: "transparent",
                          width: `${canvasSize.width}px`,
                          height: `${canvasSize.height}px`,
                        },
                      }}
                    />
                  )}

                  {!isSigned && (
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        pointerEvents: "none",
                      }}
                    >
                      <CustomTypography
                        variant="Body"
                        sx={{
                          color: theme.vars.palette.Content.SuperWeak,
                          fontWeight: 500,
                        }}
                      >
                        서명을 해주세요
                      </CustomTypography>
                    </Box>
                  )}

                  {isSigned && (
                    <Box sx={{ position: "absolute", right: 20, bottom: 18 }}>
                      <Typography
                        variant="Caption"
                        sx={{
                          textDecoration: "underline",
                          cursor: "pointer",
                          textUnderlineOffset: "2px",
                          color: theme.vars.palette.Content.Weak,
                          fontWeight: 500,
                        }}
                        onClick={handleClear}
                      >
                        다시 쓰기
                      </Typography>
                    </Box>
                  )}
                  <Box
                    sx={{
                      position: "absolute",
                      left: 20,
                      bottom: 18,
                      pointerEvents: "none",
                    }}
                  >
                    <CustomTypography
                      variant="Caption"
                      sx={{
                        color: theme.vars.palette.Content.Weak,
                        fontWeight: 500,
                      }}
                    >
                      {`${displayDate}`}
                    </CustomTypography>
                  </Box>
                </Box>
              </Box>
            )}
          </Stack>
        </Stack>
      </Stack>
      <BottomNextButton
        disabled={submitDisabled}
        loading={isSubmitLoading}
        onClick={handleSubmitSignature}
        text={"약속하기"}
      />
    </Box>
  );
  return (
    <FullPageWithHeaderAndProgressV2
      hideHeader={true}
      hideProgress={true}
      body={body}
      disableBack={true}
      showClose={false}
    />
  );
}
